import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { MetroImage } from 'components/city-blocks/metro-image';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import { map } from 'lodash';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { getRegionDisplayName } from 'shared/baby-grammar-rules';
import { metroRootBreadcrumbs } from 'utils/breadcrumb-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedRegions } from 'utils/memoized-data-structure';
import { encodeRegionPageHref } from 'utils/urls';

interface IProps {
  regions: RegionInfoFragment[];
}

const METRO_HOME_META_DESC =
  'Shef delivers authentic cuisines in major metros across the States. Check to see if Shef delivers delicious homemade food in your area!';
const METRO_HOME_HEADER_COPY_TITLE = 'We Deliver Homemade Food in the Following Metros';
const METRO_HOME_HEADER_COPY_CONTENT = (
  <span>
    Shef delivers delicious homemade food from local Shefs. Check out our metropolitan area coverage below! We are
    rapidly expanding and will be adding additional metros soon! Check back if we don't currently deliver in your area,
    or{' '}
    <Link
      href='https://help.shef.com/en/'
      className='text-neutral-500 underline underline-offset-2 hover:text-neutral-700'>
      contact us
    </Link>{' '}
    to see when we might be near you!
  </span>
);

const MetrosWeServe: React.FC<IProps> = ({ regions }) => {
  const title = 'See All Metropolitan Areas Where Our Shefs Cook Homemade Food!';
  return (
    <Layout>
      <Head>
        <title>{title}</title>
        <meta name='og:title' content={title} />
        <meta name='description' content={METRO_HOME_META_DESC} />
      </Head>
      <HeroBlock className='mb-10' heroText='Metro Areas Where Shef Delivers' crumbs={metroRootBreadcrumbs} />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy className='mb-12' title={METRO_HOME_HEADER_COPY_TITLE} content={METRO_HOME_HEADER_COPY_CONTENT} />

        <div className='mb-20 flex flex-col flex-wrap gap-4 md:flex-row'>
          {map(regions, (region) => (
            <Link
              key={region.id}
              href={encodeRegionPageHref(region)}
              className='flex w-full flex-row items-center rounded-lg border border-neutral-100 p-4 pt-3 md:w-[192px] md:flex-col'>
              <MetroImage region={region} className='mr-6 md:mb-4 md:mr-0 ' />
              <div className='flex flex-col md:items-center'>
                <span className='mb-2 text-2xl font-bold'>{getRegionDisplayName(region)}</span>
                <span className='flex flex-row items-center text-neutral-800'>
                  <span className='text-sm font-semibold'>Explore</span>
                  <i className='far fa-chevron-right ml-2 text-[10px]' />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </MaxWidthContainer>
    </Layout>
  );
};

export async function getStaticProps() {
  // Load/Re-compute memoized data structures
  return {
    props: { regions: await memoizedRegions() },
  };
}

export default MetrosWeServe;
